/**
 * Live announcements — Step 51.
 *
 * Two presentations of one component, because the two audiences meet them
 * differently:
 *
 *  - `bar` — a strip across the top of the agent shell. Agents live in the
 *    queue all day, so it has to be glanceable and stay out of the way.
 *  - `card` — stacked inside the portal's own content column (Kai: *"maybe it
 *    can be within the page for the requester portal end"*). A full-bleed
 *    strip above a branded header reads as a system fault on a page a
 *    customer visits twice a month; inside the column it reads as news.
 *
 * It must not look like an error (Kai: *"it looks like an error, rather than
 * an informative alert"*). So: the surface stays neutral, the MEGAPHONE is
 * the constant — the same icon at every severity, because the thing being
 * said is "somebody is telling you something", not "something failed" — and
 * severity is a tinted chip plus a word, never a wash across the whole bar.
 *
 * The body is clamped to two lines with a measured expander so a long notice
 * cannot shove the app below the fold, and only the loudest shows until you
 * ask for the rest. Three stacked bars is a wall, and people stop reading
 * walls — including the critical one at the bottom.
 *
 * Dismissible on purpose — a banner you cannot get rid of is one people learn
 * to look past, which defeats it. The dismissal records the VERSION, so
 * editing the announcement brings it back, because an edit is new
 * information.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Megaphone, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { relativeTime } from '../../lib/time';
import { cn } from '../../lib/utils';

export interface Announcement {
  id: string;
  title: string;
  bodyHtml: string | null;
  severity: 'info' | 'warning' | 'critical';
  startsAt: number;
  endsAt: number | null;
  version: number;
}

/**
 * `chip` is the one saturated accent per row (the standing rule). `label` is
 * the redundant signal so severity never rides on colour alone — and `info`
 * has none, because "this is a notice" is what the megaphone already says
 * and a badge reading NOTICE on every routine post is just ink.
 */
export const SEVERITY = {
  info: { label: null, chip: 'bg-brand/15 text-brand' },
  warning: { label: 'Heads up', chip: 'bg-amber-500/20 text-amber-700 dark:text-amber-400' },
  critical: { label: 'Important', chip: 'bg-destructive/15 text-destructive' },
} as const;

/** Loudest first — a critical sitting behind an info is one nobody reads. */
const RANK = { critical: 3, warning: 2, info: 1 } as const;

export function useLiveAnnouncements() {
  return useQuery({
    queryKey: qk.announcements.live,
    queryFn: () => apiFetch<{ entries: Announcement[] }>('/api/announcements/live'),
    staleTime: 60_000,
  });
}

export function AnnouncementBanner() {
  const qc = useQueryClient();
  const q = useLiveAnnouncements();
  const dismiss = useMutation({
    mutationFn: (id: string) => apiSend(`/api/announcements/${id}/dismiss`, 'POST'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.announcements.live }),
  });
  return <AnnouncementStrip entries={q.data?.entries ?? []} onDismiss={(id) => dismiss.mutate(id)} />;
}

/**
 * The rendering half, shared by both domains. Agents and end users see the
 * same words — Kai: announcements are *"mainly for end users, rather than
 * agents, but both should see them"* — so the markup lives once and each
 * domain brings its own fetch/dismiss pair (three doors, never crossed).
 */
export function AnnouncementStrip({
  entries,
  onDismiss,
  variant = 'bar',
}: {
  entries: Announcement[];
  onDismiss: (id: string) => void;
  variant?: 'bar' | 'card';
}) {
  const [showAll, setShowAll] = useState(false);

  if (!entries.length) return null;

  const ordered = [...entries].sort((a, b) => RANK[b.severity] - RANK[a.severity] || b.startsAt - a.startsAt);
  const shown = showAll ? ordered : ordered.slice(0, 1);
  const hidden = ordered.length - 1;

  const more = hidden > 0 && (
    <button
      type="button"
      onClick={() => setShowAll((v) => !v)}
      className={cn(
        'flex w-full items-center justify-center gap-1 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        variant === 'bar' && 'border-t bg-muted/40 hover:bg-muted',
      )}
    >
      <ChevronDown className={cn('h-3 w-3 transition-transform', showAll && 'rotate-180')} />
      {showAll ? 'Show fewer' : `${hidden} more ${hidden === 1 ? 'announcement' : 'announcements'}`}
    </button>
  );

  if (variant === 'card') {
    return (
      <div className="mb-6 space-y-2">
        {shown.map((a) => (
          <div key={a.id} className="overflow-hidden rounded-lg border bg-card shadow-sm">
            <AnnouncementRow a={a} onDismiss={onDismiss} />
          </div>
        ))}
        {more}
      </div>
    );
  }

  return (
    <div className="shrink-0 border-b bg-card">
      {shown.map((a) => (
        <div key={a.id} className="border-t first:border-t-0">
          <AnnouncementRow a={a} onDismiss={onDismiss} />
        </div>
      ))}
      {more}
    </div>
  );
}

function AnnouncementRow({ a, onDismiss }: { a: Announcement; onDismiss: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const s = SEVERITY[a.severity] ?? SEVERITY.info;

  // Measured, not guessed. A character count gets this wrong in both
  // directions — it offered "Read more" on a notice that already fitted at
  // 1440px, and would hide the expander on a narrow window where the same
  // text needs four lines. Re-measured on resize for exactly that reason.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || open) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, a.bodyHtml]);

  return (
    <div className="flex items-start gap-3 px-4 py-2.5 text-sm">
      <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md', s.chip)}>
        <Megaphone className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-medium">{a.title}</span>
          {s.label && (
            <span
              className={cn(
                'rounded px-1.5 py-px text-[11px] font-medium leading-4',
                a.severity === 'critical'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
              )}
            >
              {s.label}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{relativeTime(a.startsAt)}</span>
        </div>
        {a.bodyHtml && (
          <div
            ref={bodyRef}
            className={cn(
              'prose prose-sm dark:prose-invert mt-0.5 max-w-none overflow-x-auto text-muted-foreground prose-p:my-0.5',
              !open && 'line-clamp-2',
            )}
            // Sanitised on WRITE, never on render — the standing rule.
            dangerouslySetInnerHTML={{ __html: a.bodyHtml }}
          />
        )}
        {(overflows || open) && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1 rounded text-xs font-medium underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {open ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={() => onDismiss(a.id)}
        aria-label={`Dismiss ${a.title}`}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
